import React from "react";
import Dropdowns from "../../components/Dropdowns";
import { useBreadcrumbs } from "contexts/BreadcrumbsContext";
import { useCustomerDataFilters } from "contexts/CustomerDataFiltersContext";
import { BranchDetails, BranchNetwork, fetchBranchDetails, fetchBranchNetworkData } from "./api";
import BranchNetworkMap from "./components/BranchNetworkMap";
import BranchDetailsPanel from "./components/BranchDetailsPanel";

const BranchNetworkPage: React.FC = () => {
    const { setBreadcrumbs } = useBreadcrumbs();
    const { years, year, levels, loading, error, handleSelectChange, handleBatchSelect, handleYearChange, getCurrentFilters } = useCustomerDataFilters();

    const [dataLoading, setDataLoading] = React.useState<boolean>(false);
    const [branches, setBranches] = React.useState<BranchNetwork[]>([]);
    const [selectedBranch, setSelectedBranch] = React.useState<BranchDetails | null>(null);
    const [legend, setLegend] = React.useState<Record<string, string>>({});
    const [legendLevel, setLegendLevel] = React.useState<string>("");

    const fetchData = React.useCallback(async () => {
        const filters = getCurrentFilters();
        if (!filters) return;

        setDataLoading(true);

        try {
            const data = await fetchBranchNetworkData({filters});

            setBranches(data.branches ?? []);
            setLegend(data.legend ?? {});
            setLegendLevel(data.legend_level ?? "");
        } catch (err) {
            console.error("Failed to fetch branch network data", err);
        } finally {
            setDataLoading(false);
        }
    }, [getCurrentFilters]);

    const handleBranchClick = React.useCallback(async (ouCode: string) => {
        try {
            const data = await fetchBranchDetails(ouCode);
            console.log("Branch details response:", data);
            setSelectedBranch(data);
        } catch (err) {
            console.error("Failed to fetch branch details", err);
        }
    }, []);


    React.useEffect(() => {
        setBreadcrumbs([
            { label: "Reports", href: "/reports/pnl" },
            { label: "Branch Network" },
        ]);
    }, [setBreadcrumbs]);

    React.useEffect(() => {
        if (!year) return;
        if (levels.length === 0) return;

        fetchData();
    }, [year, levels, fetchData]);

    return (
        <div className="space-y-6 mx-auto w-full max-w-screen-2xl 2xl:max-w-[2000px] px-4 sm:px-6">
            <div className="space-y-4">
                <Dropdowns
                    year={year}
                    years={years}
                    levels={levels}
                    loading={loading}
                    onSelectChange={handleSelectChange}
                    onBatchSelect={handleBatchSelect}
                    onYearChange={handleYearChange}
                />

                <div className="grid grid-cols-12 gap-4 h-[calc(100vh-220px)]">
                    <div className="col-span-6 bg-white border rounded relative">
                        <BranchNetworkMap
                            branches={branches}
                            legend={legend}
                            legendLevel={legendLevel}
                            onBranchClick={handleBranchClick}
                        />
                    </div>

                    <div className="col-span-6 bg-white border flex flex-col min-h-0 rounded p-4">
                        <BranchDetailsPanel data={selectedBranch} />
                    </div>
                </div>

                {error && (
                    <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-red-600 text-sm">{error}</p>
                    </div>
                )}

                {dataLoading && (
                    <p className="text-sm text-gray-500">
                        Loading branch network...
                    </p>
                )}
            </div>
        </div>
    );
};

export default BranchNetworkPage;
